import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import type { FinancialConfig } from "@/lib/calc";
import { recalcRecord, resolveCertYear } from "@/lib/calc/record";
import type { PastedAreaRow } from "../components/insurance/form/areas-paste";
import {
  createEmptyArea,
  createEmptyFacility,
  createEmptyUnit,
  defaultFormValues,
} from "../components/insurance/form/defaults";
import { FormActionBar } from "../components/insurance/form/form-action-bar";
import { SectionAreasForm } from "../components/insurance/form/section-areas-form";
import { SectionAttachmentsForm } from "../components/insurance/form/section-attachments-form";
import { SectionCertificateForm } from "../components/insurance/form/section-certificate-form";
import { SectionDamageForm } from "../components/insurance/form/section-damage-form";
import { SectionDescriptionForm } from "../components/insurance/form/section-description-form";
import { SectionFacilitiesForm } from "../components/insurance/form/section-facilities-form";
import { SectionFinancialForm } from "../components/insurance/form/section-financial-form";
import { SectionImagesForm } from "../components/insurance/form/section-images-form";
import { SectionInsuredForm } from "../components/insurance/form/section-insured-form";
import {
  SectionFslForm,
  SectionHousekeepingForm,
  SectionNotesForm,
  SectionSignatureForm,
  SectionSupersedingForm,
} from "../components/insurance/form/section-remaining-form";
import { SectionSummaryForm } from "../components/insurance/form/section-summary-form";
import { SectionUnitsForm } from "../components/insurance/form/section-units-form";
import type {
  AreaFormRow,
  FacilityCategory,
  FacilityFormRow,
  InsuranceFormData,
  UnitFormRow,
} from "../components/insurance/form/types";
import { SectionNav } from "../components/insurance/view/section-nav";
import { useConfirm } from "../components/ui/confirm-dialog";
import { ErrorState, PageLoading } from "../components/ui/loading";
import {
  useCreateInsuranceRecord,
  useDeleteInsuranceRecord,
  useGlobalSettings,
  useInsuranceRecord,
  usePropertyTypes,
  useSubsoilOptions,
  useUpdateInsuranceRecord,
  useValuers,
} from "../hooks/use-api";
import { ApiError, api } from "../lib/api";
import type { DirtySnapshot } from "./insurance-form-dirty";
import { snapshotDirtyState, snapshotsDiffer } from "./insurance-form-dirty";
import { mapAreas, mapFacilities, mapRecordToForm, mapUnits, toStr } from "./insurance-form-load";
import { buildInsurancePayload } from "./insurance-form-payload";
import { formSections } from "./insurance-form-sections";

export function InsuranceFormPage(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const recordId = id ? Number(id) : null;
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: recordData, isLoading, error } = useInsuranceRecord(id);
  const { data: ptData } = usePropertyTypes();
  const { data: soData } = useSubsoilOptions();
  const { data: vData } = useValuers();
  const { data: settingsData } = useGlobalSettings();
  const gstEnabled = settingsData?.gstEnabled ?? false;

  const propertyTypes = (ptData?.items ?? []).map((i) => ({ id: String(i.id), name: i.name }));
  const subsoilOptions = (soData?.items ?? []).map((i) => ({ id: String(i.id), name: i.name }));
  const valuers = (vData?.items ?? []).map((i) => ({
    id: String(i.id),
    name: i.name,
    qualifications: i.qualifications,
  }));

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    formState: { isDirty },
  } = useForm<InsuranceFormData>({
    defaultValues: defaultFormValues,
  });

  const createRecord = useCreateInsuranceRecord();
  const updateRecord = useUpdateInsuranceRecord(id);
  const deleteRecord = useDeleteInsuranceRecord(id);
  const { confirm, dialog } = useConfirm();
  const dirtyBaselineRef = useRef<DirtySnapshot | null>(
    snapshotDirtyState({
      areas: [],
      units: [],
      facilities: [],
      imagesIntro: "",
      roundingAdjustments: { reinstatement: 0, inflation: 0, demolition: 0, drc: 0 },
    }),
  );

  const [areas, setAreas] = useState<AreaFormRow[]>([]);
  const [units, setUnits] = useState<UnitFormRow[]>([]);
  const [facilities, setFacilities] = useState<FacilityFormRow[]>([]);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("insured");
  const [imagesIntro, setImagesIntro] = useState("");
  const [dataLoaded, setDataLoaded] = useState(false);
  const [roundingAdjustments, setRoundingAdjustments] = useState({
    reinstatement: 0,
    inflation: 0,
    demolition: 0,
    drc: 0,
  });

  // Populate form when record loads
  useEffect(() => {
    if (!recordData?.record || dataLoaded) return;
    const r = recordData.record as Record<string, unknown>;
    const loadedAreas = mapAreas((r.areas as Record<string, unknown>[]) ?? []);
    const loadedUnits = mapUnits((r.units as Record<string, unknown>[]) ?? []);
    const loadedFacilities = mapFacilities((r.facilities as Record<string, unknown>[]) ?? []);
    const loadedImagesIntro = toStr(r.imagesIntro);
    const loadedRounding = {
      reinstatement: Number(r.roundingReinstatement) || 0,
      inflation: Number(r.roundingInflation) || 0,
      demolition: Number(r.roundingDemolition) || 0,
      drc: Number(r.roundingDrc) || 0,
    };
    reset(mapRecordToForm(r));
    setAreas(loadedAreas);
    setUnits(loadedUnits);
    setFacilities(loadedFacilities);
    setImagesIntro(loadedImagesIntro);
    setRoundingAdjustments(loadedRounding);
    dirtyBaselineRef.current = snapshotDirtyState({
      areas: loadedAreas,
      units: loadedUnits,
      facilities: loadedFacilities,
      imagesIntro: loadedImagesIntro,
      roundingAdjustments: loadedRounding,
    });
    setDataLoaded(true);
  }, [recordData, dataLoaded, reset]);

  // Legacy records predate geocoding: resolve missing coordinates from the
  // stored address via the lookup so imagery works without re-picking it.
  useEffect(() => {
    if (!dataLoaded) return;
    const address = getValues("address");
    if (!address || getValues("latitude") != null) return;
    const q = address.split(",").slice(0, 2).join(",").trim();
    if (q.length < 3) return;
    let cancelled = false;
    api
      .get<{ results: Array<{ address: string; lat: number; lng: number }> }>(
        `/api/address/search?q=${encodeURIComponent(q)}`,
      )
      .then((data) => {
        const top = data.results[0];
        if (!cancelled && top) {
          setValue("latitude", top.lat);
          setValue("longitude", top.lng);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [dataLoaded, getValues, setValue]);

  const r = (recordData?.record as Record<string, unknown>) ?? {};
  const supersedes = r.supersedes as Record<string, unknown> | null;
  const supersededBy = r.supersededBy as Record<string, unknown> | null;
  const valuer = r.valuer as Record<string, unknown> | null;

  // Build financial config from watched form values
  const formValues = watch();
  const financialConfig: FinancialConfig = useMemo(
    () => ({
      eModal: formValues.eModal ?? 0,
      feesPercentage: formValues.feesPercentage ?? 0,
      demolitionPercentage: formValues.demolitionPercentage ?? 0,
      gstRate: formValues.gst ?? 15,
      firstYearMonths: formValues.firstYearMonths ?? 12,
      demoPlanMonths: formValues.inflationDemoPlanMonths ?? 0,
      constructionMonths: formValues.inflationConstructionMonths ?? 0,
      inflationYear1: formValues.inflationYear1 ?? 0,
      inflationYear2: formValues.inflationYear2 ?? 0,
      inflationYear3: formValues.inflationYear3 ?? 0,
      inflationYear4: formValues.inflationYear4 ?? 0,
    }),
    [
      formValues.eModal,
      formValues.feesPercentage,
      formValues.demolitionPercentage,
      formValues.gst,
      formValues.firstYearMonths,
      formValues.inflationDemoPlanMonths,
      formValues.inflationConstructionMonths,
      formValues.inflationYear1,
      formValues.inflationYear2,
      formValues.inflationYear3,
      formValues.inflationYear4,
    ],
  );

  const certYear = resolveCertYear(formValues.certificateDate, formValues.pocFrom);

  const damageInput = formValues.damageAmount
    ? {
        damageAmount: formValues.damageAmount ?? 0,
        changePercentage: formValues.damageChangePercentage ?? 0,
        firstYearMonths: formValues.damageFirstYearMonths,
        demoPlanMonths: formValues.damageDemoPlanMonths,
        constructionMonths: formValues.damageConstructionMonths,
        inflationYear1Pct: formValues.damageInflationYear1Pct,
        inflationYear2Pct: formValues.damageInflationYear2Pct,
        inflationYear3Pct: formValues.damageInflationYear3Pct,
        inflationYear4Pct: formValues.damageInflationYear4Pct,
      }
    : null;

  const calc = useMemo(
    () =>
      recalcRecord({
        config: financialConfig,
        certYear,
        areas: areas.map((row) => ({
          area: row.area ?? 0,
          multiple: row.multiple ?? 0,
          rate: row.rate,
          rateOverride: row.rateOverride,
          addGst: row.addGst,
          eyrBuilt: row.eyrBuilt ?? formValues.eyrBuilt ?? 0,
          firstYearMonths: row.firstYearMonths,
          demoPlanMonths: row.demoPlanMonths,
          constructionMonths: row.constructionMonths,
          inflationYear1Pct: row.inflationYear1Pct,
          inflationYear2Pct: row.inflationYear2Pct,
          inflationYear3Pct: row.inflationYear3Pct,
          inflationYear4Pct: row.inflationYear4Pct,
          demolitionPercentage: row.demolitionPercentage,
          percentagePa: row.percentagePa ?? 0,
        })),
        facilities: facilities.map((row) => ({
          area: row.area ?? 0,
          multiple: row.multiple ?? 0,
          rate: row.rate,
          rateOverride: row.rateOverride,
          addGst: row.addGst,
          firstYearMonths: row.firstYearMonths,
          demoPlanMonths: row.demoPlanMonths,
          constructionMonths: row.constructionMonths,
          inflationYear1Pct: row.inflationYear1Pct,
          inflationYear2Pct: row.inflationYear2Pct,
          inflationYear3Pct: row.inflationYear3Pct,
          inflationYear4Pct: row.inflationYear4Pct,
          demolitionPercentage: row.demolitionPercentage,
        })),
        damage: damageInput,
        rounding: roundingAdjustments,
        numberOfUnits: formValues.numberOfUnits || 1,
      }),
    [
      areas,
      facilities,
      financialConfig,
      certYear,
      roundingAdjustments,
      formValues.numberOfUnits,
      formValues.eyrBuilt,
      damageInput,
    ],
  );

  const damageCalc = calc.damage;
  const summary = calc.summary;

  const calcedAreas: AreaFormRow[] = areas.map((row, i) => {
    const c = calc.areas[i];
    return {
      ...row,
      calcRate: c?.rate ?? null,
      calcReinstatementExcl: c?.reinstatementExclFees ?? null,
      calcReinstatementIncl: c?.reinstatementInclFees ?? null,
      calcReinstatementMonths: c?.reinstatementMonths ?? null,
      calcInflationAmtYear1: c?.inflationAmtYear1 ?? null,
      calcInflationAmtYear2: c?.inflationAmtYear2 ?? null,
      calcInflationAmtYear3: c?.inflationAmtYear3 ?? null,
      calcInflationAmtYear4: c?.inflationAmtYear4 ?? null,
      calcInflationTotal: c?.reinstatementInflationTotal ?? null,
      calcDemolitionCost: c?.demolitionCost ?? null,
      calcDemolitionRate: c?.demolitionRate ?? null,
      calcDepreciation: c?.depreciation ?? null,
      calcDepreciationTotalPct: c?.depreciationTotalPct ?? null,
      calcDrcForFsl: c?.drcForFsl ?? null,
    };
  });

  const calcedFacilities: FacilityFormRow[] = facilities.map((row, i) => {
    const c = calc.facilities[i];
    return {
      ...row,
      calcRate: c?.rate ?? null,
      calcReinstatementExcl: c?.reinstatementExclFees ?? null,
      calcReinstatementIncl: c?.reinstatementInclFees ?? null,
      calcReinstatementMonths: c?.reinstatementMonths ?? null,
      calcInflationAmtYear1: c?.inflationAmtYear1 ?? null,
      calcInflationAmtYear2: c?.inflationAmtYear2 ?? null,
      calcInflationAmtYear3: c?.inflationAmtYear3 ?? null,
      calcInflationAmtYear4: c?.inflationAmtYear4 ?? null,
      calcInflationTotal: c?.reinstatementInflationTotal ?? null,
      calcDemolitionCost: c?.demolitionCost ?? null,
      calcDemolitionRate: c?.demolitionRate ?? null,
    };
  });

  // Track unsaved edits: react-hook-form's isDirty covers the registered
  // fields, plus a snapshot diff for the areas/units/facilities/rounding/
  // imagesIntro state that lives outside the form.
  const currentDirtySnapshot = useMemo(
    () => snapshotDirtyState({ areas, units, facilities, imagesIntro, roundingAdjustments }),
    [areas, units, facilities, imagesIntro, roundingAdjustments],
  );
  const hasUnsavedChanges = isDirty || snapshotsDiffer(dirtyBaselineRef.current, currentDirtySnapshot);

  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges]);

  // Scroll-based active section tracking
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleScroll = (): void => {
      const containerRect = container.getBoundingClientRect();
      const threshold = containerRect.top + 80;

      let current = formSections[0]?.id ?? "";
      for (const section of formSections) {
        const el = document.getElementById(section.id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= threshold) {
          current = section.id;
        }
      }
      setActiveSection(current);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = useCallback((sectionId: string) => {
    const container = scrollRef.current;
    const el = document.getElementById(sectionId);
    if (!container || !el) return;

    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const offset = elRect.top - containerRect.top + container.scrollTop;

    container.scrollTo({ top: offset, behavior: "smooth" });
  }, []);

  const addArea = useCallback(() => {
    setAreas((prev) => [...prev, { ...createEmptyArea(), eyrBuilt: prev[0]?.eyrBuilt ?? null }]);
  }, []);

  const removeArea = useCallback((index: number) => {
    setAreas((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // eyrBuilt on row 1 propagates to every row still following it; editing a
  // follower's own eyrBuilt directly detaches it from row 1.
  const updateArea = useCallback((index: number, field: keyof AreaFormRow, value: unknown) => {
    setAreas((prev) => {
      if (field !== "eyrBuilt") return prev.map((row, i) => (i === index ? { ...row, [field]: value } : row));
      if (index === 0) {
        return prev.map((row, i) =>
          i === 0
            ? { ...row, eyrBuilt: value as number | null }
            : row.eyrFollowsFirst
              ? { ...row, eyrBuilt: value as number | null }
              : row,
        );
      }
      return prev.map((row, i) =>
        i === index ? { ...row, eyrBuilt: value as number | null, eyrFollowsFirst: false } : row,
      );
    });
  }, []);

  const pasteAreaRows = useCallback((startIndex: number, rows: PastedAreaRow[]) => {
    setAreas((prev) => {
      const next = [...prev];
      const firstEyr = next[0]?.eyrBuilt ?? null;
      rows.forEach((pasted, offset) => {
        const idx = startIndex + offset;
        const base = next[idx] ?? createEmptyArea();
        const eyrFollowsFirst = pasted.eyrBuilt == null;
        const eyrBuilt = pasted.eyrBuilt ?? (idx === 0 ? base.eyrBuilt : firstEyr);
        next[idx] = { ...base, description: pasted.description, area: pasted.area, eyrBuilt, eyrFollowsFirst };
      });
      return next;
    });
  }, []);

  const addUnit = useCallback(() => {
    setUnits((prev) => [...prev, createEmptyUnit(prev.length + 1)]);
  }, []);

  const removeUnit = useCallback((index: number) => {
    setUnits((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateUnit = useCallback((index: number, field: keyof UnitFormRow, value: unknown) => {
    setUnits((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  }, []);

  const addFacility = useCallback((category: FacilityCategory) => {
    setFacilities((prev) => [...prev, createEmptyFacility(category)]);
  }, []);

  const removeFacility = useCallback((index: number) => {
    setFacilities((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateFacility = useCallback((index: number, field: keyof FacilityFormRow, value: unknown) => {
    setFacilities((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  }, []);

  const markSaved = useCallback(() => {
    reset(getValues());
    dirtyBaselineRef.current = snapshotDirtyState({ areas, units, facilities, imagesIntro, roundingAdjustments });
  }, [reset, getValues, areas, units, facilities, imagesIntro, roundingAdjustments]);

  const onSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const payload = buildInsurancePayload({
        formValues,
        imagesIntro,
        areas: calcedAreas,
        units,
        facilities: calcedFacilities,
        damageCalc,
        summary,
        roundingAdjustments,
      });

      if (isEdit) {
        const version = Number(r.version) || 1;
        const { record } = await updateRecord.mutateAsync({ ...payload, version });
        setLastSaved(new Date());
        markSaved();
        toast.success("Record saved");
        return record;
      }

      const { record } = await createRecord.mutateAsync(payload);
      setLastSaved(new Date());
      markSaved();
      toast.success("Record saved");
      navigate(`/insurance/${(record as { id: number }).id}`);
      return record;
    } catch (err) {
      if (err instanceof ApiError && err.code === "VERSION_CONFLICT") {
        toast.error("This record was changed by someone else since you opened it. Reload to get the latest version.", {
          action: {
            label: "Reload",
            onClick: () => window.location.reload(),
          },
        });
      } else {
        toast.error(err instanceof Error ? err.message : "Failed to save record");
      }
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [
    isEdit,
    formValues,
    imagesIntro,
    calcedAreas,
    units,
    calcedFacilities,
    damageCalc,
    summary,
    roundingAdjustments,
    r.version,
    updateRecord,
    createRecord,
    navigate,
    markSaved,
  ]);

  const backPath = recordId ? `/insurance/${recordId}` : "/insurance";

  const onCancel = useCallback(async () => {
    if (hasUnsavedChanges) {
      const ok = await confirm({
        title: "Discard unsaved changes?",
        description: "You have unsaved changes on this record. Leaving now will discard them.",
        destructive: true,
      });
      if (!ok) return;
    }
    navigate(backPath);
  }, [hasUnsavedChanges, confirm, navigate, backPath]);

  const onDelete = useCallback(async () => {
    const ok = await confirm({
      title: "Delete record",
      description: "This soft-deletes the record. It can be restored by an admin.",
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteRecord.mutateAsync();
      toast.success("Record deleted");
      navigate("/insurance");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete record");
    }
  }, [confirm, deleteRecord, navigate]);

  if (isEdit && isLoading) return <PageLoading />;
  if (isEdit && (error || !recordData?.record)) return <ErrorState message="Record not found or failed to load." />;

  const title = isEdit ? toStr(r.address) || `Edit Record #${id}` : "New Insurance Record";

  return (
    <div className="-my-6">
      {dialog}
      {/* Fixed header: title + action bar */}
      <div className="sticky top-0 z-30 border-b bg-background px-1 py-3">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-2xl font-bold tracking-tight truncate">{title}</h1>
          <FormActionBar
            recordId={recordId}
            lastSaved={lastSaved}
            isSaving={isSaving}
            onSave={onSave}
            onCancel={onCancel}
            onDelete={isEdit ? onDelete : undefined}
          />
        </div>
      </div>

      <form onSubmit={handleSubmit(onSave)} className="flex items-start gap-4 pt-4">
        {/* Left column: section nav — fixed, doesn't scroll */}
        <div className="hidden lg:block w-[180px] shrink-0 sticky top-[84px] self-start max-h-[calc(100vh-150px)] overflow-y-auto">
          <SectionNav
            sections={formSections.map((s) =>
              s.id === "photos" || s.id === "attachments" ? { ...s, visible: isEdit } : s,
            )}
            activeSection={activeSection}
            onNavigate={scrollToSection}
          />
        </div>

        {/* Right column: scrollable form sections */}
        <div ref={scrollRef} className="flex-1 min-w-0 space-y-5 pr-1">
          <SectionInsuredForm register={register} watch={watch} setValue={setValue} />
          <SectionCertificateForm
            register={register}
            setValue={setValue}
            watch={watch}
            propertyTypes={propertyTypes}
            subsoilOptions={subsoilOptions}
            valuers={valuers}
          />
          <SectionUnitsForm units={units} onAdd={addUnit} onRemove={removeUnit} onUpdate={updateUnit} />
          <SectionDescriptionForm register={register} values={getValues()} />
          <SectionImagesForm
            recordId={isEdit && recordId ? String(recordId) : undefined}
            latitude={formValues.latitude ?? null}
            longitude={formValues.longitude ?? null}
            imagesIntro={imagesIntro}
            onImagesIntroChange={setImagesIntro}
          />
          <SectionFinancialForm register={register} watch={watch} setValue={setValue} />
          <SectionAreasForm
            areas={calcedAreas}
            onAdd={addArea}
            onRemove={removeArea}
            onUpdate={updateArea}
            onPasteRows={pasteAreaRows}
            gstEnabled={gstEnabled}
          />
          <SectionFacilitiesForm
            id="facilities-recreational"
            title="Recreational Facilities"
            category="RECREATIONAL"
            facilities={calcedFacilities}
            onAdd={() => addFacility("RECREATIONAL")}
            onRemove={removeFacility}
            onUpdate={updateFacility}
            gstEnabled={gstEnabled}
          />
          <SectionFacilitiesForm
            id="facilities-special"
            title="Special Features"
            category="SPECIAL_FEATURE"
            facilities={calcedFacilities}
            onAdd={() => addFacility("SPECIAL_FEATURE")}
            onRemove={removeFacility}
            onUpdate={updateFacility}
            gstEnabled={gstEnabled}
          />
          <SectionDamageForm register={register} watch={watch} setValue={setValue} damageCalc={damageCalc} />
          {isEdit && recordId ? (
            <SectionAttachmentsForm recordId={String(recordId)} />
          ) : (
            <p className="text-sm text-muted-foreground px-1">Save the record first to add attachments.</p>
          )}
          <SectionSummaryForm
            summary={summary}
            roundingAdjustments={roundingAdjustments}
            onRoundingChange={(f, v) => setRoundingAdjustments((prev) => ({ ...prev, [f]: v }))}
          />
          <SectionFslForm register={register} />
          <SectionNotesForm register={register} />
          <SectionSignatureForm
            signatureUrl={valuer ? toStr(valuer.signatureUrl) || null : null}
            valuerName={valuer ? toStr(valuer.name) || null : null}
          />
          <SectionHousekeepingForm register={register} watch={watch} setValue={setValue} />
          <SectionSupersedingForm
            isEdit={isEdit}
            supersedesId={supersedes ? Number(supersedes.id) : null}
            supersedesAddress={supersedes ? toStr(supersedes.address) : null}
            supersededById={supersededBy ? Number(supersededBy.id) : null}
            supersededByAddress={supersededBy ? toStr(supersededBy.address) : null}
          />
          {/* Spacer so bottom sections can scroll to top */}
          <div className="h-[60vh] shrink-0" />
        </div>
      </form>
    </div>
  );
}
